import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db6cnha2sgkaik1undng";

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFeedback("感谢你的反馈，我们已收到！");
      form.reset();
      return;
    }

    formData.delete("company_alt");

    setStatus("submitting");
    setFeedback("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        setFeedback("感谢你的反馈，我们已收到！");
        form.reset();
      } else {
        setStatus("error");
        setFeedback(serverMsg || "提交失败，请稍后重试。");
      }
    } catch (err) {
      setStatus("error");
      setFeedback(err instanceof Error ? err.message : "网络异常，请稍后重试。");
    }
  };

  return (
    <form
      id="feedback-form"
      data-readdy-form
      action={SUBMIT_ADDR}
      method="post"
      onSubmit={handleSubmit}
      className="contact-feedback-form bg-background-50 border border-background-200 rounded-lg p-5 md:p-7"
    >
      <h2 className="font-heading font-bold text-xl text-foreground-950 flex items-center gap-2 mb-1">
        <i className="ri-chat-smile-2-line text-primary-500"></i>
        意见反馈
      </h2>
      <p className="text-sm text-foreground-500 mb-5">
        有任何建议或想看的动漫，欢迎告诉我们，我们会尽快回复。
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="fb-name" className="block text-sm text-foreground-700 mb-1.5">
            昵称 <span className="text-primary-500">*</span>
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            required
            placeholder="请输入你的昵称"
            className="w-full px-3.5 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors placeholder:text-foreground-400"
          />
        </div>

        <div>
          <label htmlFor="fb-email" className="block text-sm text-foreground-700 mb-1.5">
            邮箱 <span className="text-primary-500">*</span>
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="用于接收我们的回复"
            className="w-full px-3.5 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors placeholder:text-foreground-400"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-topic" className="block text-sm text-foreground-700 mb-1.5">
          反馈类型
        </label>
        <select
          id="fb-topic"
          name="topic"
          defaultValue="功能建议"
          className="w-full px-3.5 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors cursor-pointer"
        >
          <option value="功能建议">功能建议</option>
          <option value="内容反馈">内容反馈</option>
          <option value="播放问题">播放问题</option>
          <option value="合作洽谈">合作洽谈</option>
          <option value="其他">其他</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-message" className="block text-sm text-foreground-700 mb-1.5">
          留言内容 <span className="text-primary-500">*</span>
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          placeholder="请描述你的建议或问题（最多 500 字）"
          className="w-full px-3.5 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors resize-none placeholder:text-foreground-400"
        ></textarea>
      </div>

      {/* 反垃圾字段 */}
      <input
        type="text"
        name="company_alt"
        className="field-remark-extra"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-5 flex flex-col sm:flex-row items-center gap-3">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="w-full sm:w-auto px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === "submitting" ? "提交中..." : "提交反馈"}
        </button>
        <p className="text-xs text-foreground-400">我们尊重你的隐私，信息仅用于回复沟通。</p>
      </div>

      {status === "success" && (
        <div className="mt-4 px-4 py-3 rounded-md bg-primary-50 border border-primary-200 text-sm text-primary-700 flex items-center gap-2">
          <i className="ri-checkbox-circle-fill"></i>
          感谢你的反馈，我们已收到！
        </div>
      )}
      {status === "error" && (
        <div className="mt-4 px-4 py-3 rounded-md bg-accent-50 border border-accent-300 text-sm text-accent-900 flex items-center gap-2">
          <i className="ri-error-warning-fill"></i>
          {feedback || "提交失败，请稍后重试。"}
        </div>
      )}
    </form>
  );
}